import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { api } from '../data/api'
import { loadRazorpayCheckoutScript } from '../data/razorpay'
import BillingDetailsForm from './billingDetailsForm'

const MAX_CUSTOM_TOPUP_INR = 10000

function fmtCredits(n) {
  return `₹${(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
}

export default function BuyCreditsModal({ user, updateUser, showToast, onClose }) {
  const [packages, setPackages] = useState(null)
  const [buying,   setBuying]   = useState(false)
  const [done,     setDone]     = useState(null) // { creditsAmount, chargeAmount? } once a purchase succeeds
  const [customAmount, setCustomAmount] = useState('')

  const [voucherInput,   setVoucherInput]   = useState('')
  // A voucher is a fixed, self-contained offer (its own price + discount) —
  // not a % modifier on whatever amount the buyer separately picks. That's
  // deliberate: it caps RADTAR's exposure per redemption, since there's no
  // buyer-chosen amount for the discount to apply against.
  const [voucher,        setVoucher]        = useState(null) // { code, name, discountPct, price, chargeAmount } once applied
  const [voucherError,   setVoucherError]   = useState('')
  const [applyingVoucher, setApplyingVoucher] = useState(false)

  const needsBillingDetails = !user.billingDetails?.address?.state

  useEffect(() => {
    api.get('/api/credit-packages')
      .then(setPackages)
      .catch(() => { showToast('Failed to load credit packages.', 'error'); setPackages([]) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleApplyVoucher() {
    if (!voucherInput.trim()) return
    setApplyingVoucher(true); setVoucherError('')
    try {
      const result = await api.post('/api/vouchers/validate', { code: voucherInput.trim() })
      setVoucher({ code: voucherInput.trim().toUpperCase(), ...result })
    } catch (err) {
      setVoucher(null)
      setVoucherError(err.message || 'Invalid voucher code.')
    } finally {
      setApplyingVoucher(false)
    }
  }

  function removeVoucher() {
    setVoucher(null); setVoucherInput(''); setVoucherError('')
  }

  async function handleBuy(orderBody) {
    setBuying(true)
    try {
      const order = await api.post('/api/payments/orders', { ...orderBody, voucherCode: voucher?.code })

      const scriptLoaded = await loadRazorpayCheckoutScript()
      if (!scriptLoaded) {
        showToast('Could not load the payment widget — check your connection and try again.', 'error')
        setBuying(false)
        return
      }

      const rzp = new window.Razorpay({
        key: order.keyId,
        amount: order.amountPaise,
        currency: order.currency,
        order_id: order.orderId,
        name: 'SkillAssess',
        description: order.package.label,
        prefill: { name: user.name, email: user.email },
        theme: { color: '#0ea5e9' },
        handler: async (response) => {
          try {
            const result = await api.post(`/api/payments/${order.paymentId}/confirm`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            })
            if (result.creditsBalance != null) updateUser({ ...user, creditsBalance: result.creditsBalance })
            setDone({ creditsAmount: order.package.creditsAmount, voucherApplied: order.voucherApplied })
          } catch (err) {
            showToast(err.message || 'Payment verification failed.', 'error')
          } finally {
            setBuying(false)
          }
        },
        modal: {
          ondismiss: () => setBuying(false), // Payment stays 'created' — harmless to abandon
        },
      })
      rzp.on('payment.failed', () => {
        showToast('Payment failed — please try again.', 'error')
        setBuying(false)
      })
      rzp.open()
    } catch (err) {
      showToast(err.message || 'Could not start the purchase.', 'error')
      setBuying(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="card p-6 shadow-2xl animate-fade-up w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {done ? (
          <div className="text-center py-4">
            <p className="text-3xl mb-3">✅</p>
            <p className="text-base font-bold mb-1" style={{ color: 'var(--text-1)' }}>Credits added!</p>
            <p className="text-sm mb-5" style={{ color: 'var(--text-3)' }}>
              {fmtCredits(done.creditsAmount)} worth of credits have been added to your balance
              {done.voucherApplied
                ? ` — you paid ${fmtCredits(done.voucherApplied.chargeAmount)} with voucher ${done.voucherApplied.code} (${done.voucherApplied.discountPct}% off).`
                : '.'}
            </p>
            <button onClick={onClose} className="btn-primary text-sm">Done</button>
          </div>
        ) : (
          <>
            <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--brand)' }}>Buy Credits</p>
            <p className="text-sm mb-4" style={{ color: 'var(--text-3)' }}>
              {needsBillingDetails
                ? 'Add your billing address before purchasing credits.'
                : 'Select an amount to top up your balance.'}
            </p>

            {needsBillingDetails ? (
              <BillingDetailsForm user={user} updateUser={updateUser} showToast={showToast} />
            ) : !packages ? (
              <p className="text-sm py-6 text-center" style={{ color: 'var(--text-3)' }}>Loading packages…</p>
            ) : (
              <>
                <div className="mb-4">
                  {voucher ? (
                    <div className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl"
                      style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)' }}>
                      <p className="text-xs font-semibold" style={{ color: '#34d399' }}>🎟 {voucher.code} applied — {voucher.discountPct}% off</p>
                      <button onClick={removeVoucher} className="text-xs font-medium flex-shrink-0" style={{ color: 'var(--text-3)' }}>Remove</button>
                    </div>
                  ) : (
                    <div className="rounded-xl p-3" style={{ background: 'var(--surface-2)', border: '1px dashed var(--border)' }}>
                      <p className="text-xs font-medium mb-2 flex items-center gap-1.5" style={{ color: 'var(--text-2)' }}>
                        <span>🎟</span> Have a voucher code? Enter it below to redeem your offer.
                      </p>
                      <div className="flex items-center gap-2">
                        <input value={voucherInput} onChange={e => setVoucherInput(e.target.value.toUpperCase())}
                          placeholder="e.g. DIW4821"
                          className="flex-1 min-w-0 text-sm rounded-xl px-3 py-2 outline-none"
                          style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
                        <button onClick={handleApplyVoucher} disabled={applyingVoucher || !voucherInput.trim()}
                          className="btn-primary text-xs px-4 py-2 flex-shrink-0 disabled:opacity-50">
                          {applyingVoucher ? 'Checking…' : 'Apply'}
                        </button>
                      </div>
                    </div>
                  )}
                  {voucherError && <p className="text-xs mt-1.5" style={{ color: '#ef4444' }}>{voucherError}</p>}
                </div>

                {voucher ? (
                  // A voucher is a fixed, self-contained offer — the package
                  // grid and custom-amount input don't apply to it at all,
                  // so they're replaced entirely while one's active.
                  <div className="text-center py-4 rounded-xl mb-4" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                    <p className="text-xs mb-1" style={{ color: 'var(--text-3)' }}>{voucher.name}</p>
                    <p className="text-[11px] line-through" style={{ color: 'var(--text-3)' }}>{fmtCredits(voucher.price)}</p>
                    <p className="text-2xl font-black" style={{ color: 'var(--brand)' }}>{fmtCredits(voucher.chargeAmount)}</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>for {fmtCredits(voucher.price)} worth of credits</p>
                  </div>
                ) : (
                  <>
                    {packages.length === 0 ? (
                      <p className="text-sm py-4 text-center" style={{ color: 'var(--text-3)' }}>No credit packages are available right now.</p>
                    ) : (
                      // max-h-64 + overflow-y-auto keeps this section a fixed size no
                      // matter how many packages exist (10, 50, whatever) — it scrolls
                      // internally instead of growing the whole modal.
                      <div className="grid grid-cols-3 gap-1.5 max-h-64 overflow-y-auto pr-1">
                        {packages.map(pkg => (
                          <button key={pkg._id} onClick={() => handleBuy({ packageId: pkg._id })} disabled={buying}
                            className="flex items-center justify-center px-2 py-3 rounded-lg text-center transition-all disabled:opacity-50"
                            style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}
                            onMouseEnter={e => { if (!buying) e.currentTarget.style.borderColor = 'var(--brand)' }}
                            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                            {/* creditsAmount always equals priceInr (never GST/margin'd at
                                purchase time — see creditPackage.js), so showing both
                                would just repeat the same number; price alone is enough. */}
                            <p className="text-sm font-bold" style={{ color: 'var(--brand)' }}>{fmtCredits(pkg.priceInr)}</p>
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-2 my-4">
                      <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
                      <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>Or enter a custom amount</span>
                      <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
                    </div>

                    <input type="number" min="1" max={MAX_CUSTOM_TOPUP_INR} step="1" value={customAmount}
                      onChange={e => setCustomAmount(e.target.value)}
                      placeholder={`Amount in ₹ (max ${MAX_CUSTOM_TOPUP_INR.toLocaleString('en-IN')})`}
                      className="w-full text-sm rounded-xl px-3 py-2.5 outline-none"
                      style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
                    {Number(customAmount) > MAX_CUSTOM_TOPUP_INR && (
                      <p className="text-xs mt-1.5" style={{ color: '#ef4444' }}>Maximum custom amount is ₹{MAX_CUSTOM_TOPUP_INR.toLocaleString('en-IN')}.</p>
                    )}
                  </>
                )}

                <div className="flex items-center gap-2 pt-4">
                  <button onClick={onClose} className="btn-ghost text-sm flex-1 justify-center">Close</button>
                  {voucher ? (
                    <button onClick={() => handleBuy({})} disabled={buying}
                      className="btn-primary text-sm flex-1 justify-center disabled:opacity-50">
                      Buy
                    </button>
                  ) : (
                    <button
                      onClick={() => handleBuy({ customAmount: Number(customAmount) })}
                      disabled={buying || !customAmount || Number(customAmount) < 1 || Number(customAmount) > MAX_CUSTOM_TOPUP_INR}
                      className="btn-primary text-sm flex-1 justify-center disabled:opacity-50">
                      Buy
                    </button>
                  )}
                </div>
              </>
            )}

            {(needsBillingDetails || !packages) && (
              <div className="flex justify-end pt-4">
                <button onClick={onClose} className="btn-ghost text-sm">Close</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  )
}
